import { useCallback, useEffect, useRef, useState } from 'react'

// Turnstile type declarations
declare global {
  interface Window {
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'error-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
          size?: 'normal' | 'compact'
          'retry-interval'?: number
        },
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface UseTurnstileOptions {
  sitekey: string
  onSuccess: (token: string) => void
  onError?: () => void
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact'
}

export function useTurnstile({
  sitekey,
  onSuccess,
  onError,
  theme = 'light',
  size = 'normal',
}: UseTurnstileOptions) {
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string>('')

  // Callback ref that triggers re-render when container mounts
  const containerCallback = useCallback((node: HTMLDivElement | null) => {
    setContainer(node)
  }, [])

  // Load Turnstile script dynamically
  useEffect(() => {
    if (!sitekey) {
      console.error('Turnstile sitekey not configured')
      return
    }

    // Check if script is already loaded
    if (window.turnstile) {
      setScriptLoaded(true)
      return
    }

    // Check if script is already in DOM (from previous mount)
    const existingScript = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]',
    )
    if (existingScript) {
      // Wait for it to load
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          setScriptLoaded(true)
          clearInterval(checkTurnstile)
        }
      }, 100)
      return () => clearInterval(checkTurnstile)
    }

    // Load script dynamically
    const script = document.createElement('script')
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => {
      setScriptLoaded(true)
    }
    script.onerror = () => {
      console.error('Failed to load Turnstile script')
    }

    document.head.appendChild(script)

    return () => {
      // Don't remove script on unmount to allow caching
      // Just clean up widget
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = ''
      }
    }
  }, [sitekey])

  // Render Turnstile widget once script is loaded and container is available
  useEffect(() => {
    if (!scriptLoaded || !container || widgetIdRef.current) return

    if (!sitekey || !window.turnstile) return

    widgetIdRef.current = window.turnstile.render(container, {
      sitekey,
      callback: onSuccess,
      'error-callback': onError,
      theme,
      size,
      'retry-interval': 1000,
    })

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = ''
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded, container, sitekey, theme, size])

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
    }
  }

  return {
    containerRef: containerCallback,
    reset,
  }
}
